import Util from '../../shared/util/util';
import ImportMode from './ImportMode';
import {EntryChange, NuvoImporter, Row, RowChanged, ValidationResult} from './Types';

type DataImportValidatorProps = {
	nuvoImporter: NuvoImporter;
	importMode: ImportMode;
	projects: string[];
	tasks: string[];
	emails: string[];
	companyIdBase64: string | undefined;
};

export default class DataImportValidator {
	private nuvoImporter: NuvoImporter;
	private importMode: ImportMode;
	private projects: string[];
	private tasks: string[];
	private emails: string[];
	private companyIdBase64: string | undefined;

	constructor({nuvoImporter, importMode, projects, tasks, emails, companyIdBase64}: DataImportValidatorProps) {
		this.nuvoImporter = nuvoImporter;
		this.importMode = importMode;
		this.projects = projects;
		this.tasks = tasks;
		this.emails = emails;
		this.companyIdBase64 = companyIdBase64;
	}

	public registerValidationForExistingTasksAndProjects(): void {
		this.nuvoImporter.registerColumnHooks<string | undefined>('existing_project_id_or_task_id', hookedRecordValues => {
			return new Promise(resolve => {
				setTimeout(() => {
					resolve(
						hookedRecordValues.map(([cellValue, rowIndex]) => {
							const validationResult = this.validateExistingTaskOrProjectId(cellValue);
							return [
								{
									value: cellValue,
									info: validationResult,
								},
								rowIndex,
							];
						})
					);
				}, 0);
			});
		});
	}

	public registerColumnHooksTaskEmails(): void {
		this.nuvoImporter.registerColumnHooks<string | undefined>('task_owner_email', hookedRecordValues => {
			return new Promise(resolve => {
				setTimeout(() => {
					resolve(
						hookedRecordValues.map(([cellValue, rowIndex]) => {
							if (cellValue) {
								return [
									{
										value: cellValue,
										info: this.validateSingleEmail(cellValue),
									},
									rowIndex,
								];
							} else {
								return [
									{
										value: '',
										info: undefined,
									},
									rowIndex,
								];
							}
						})
					);
				}, 0);
			});
		});

		this.nuvoImporter.registerColumnHooks<string | undefined>(
			'task_assigned_comma_seperated_this.emails',
			hookedRecordValues => {
				return new Promise(resolve => {
					setTimeout(() => {
						resolve(
							hookedRecordValues.map(([cellValue, rowIndex]) => {
								return [
									{
										value: cellValue ? cellValue?.toLowerCase() : '',
										info: this.validateEmailCommaSeparatedList(cellValue),
									},
									rowIndex,
								];
							})
						);
					}, 0);
				});
			}
		);
	}

	public registerColumnHooksForExistingProjects(): void {
		this.nuvoImporter.registerColumnHooks<{existing_project_id: unknown} | undefined>(
			'existing_project_id',
			hookedRecordValues => {
				return new Promise(resolve => {
					setTimeout(() => {
						resolve(
							hookedRecordValues.map(([cellValue, rowIndex]) => {
								return [
									{
										value: cellValue?.existing_project_id, // Is this type correct?
										info: this.validateProjectId(cellValue as unknown as string),
									},
									rowIndex,
								];
							})
						);
					}, 0);
				});
			}
		);
	}

	public registerColumnHooksForTimeRegEmails(): void {
		this.nuvoImporter.registerColumnHooks<string>('time_registration_person_email', hookedRecordValues => {
			return new Promise(resolve => {
				setTimeout(() => {
					resolve(
						hookedRecordValues.map(([cellValue, rowIndex]) => {
							if (cellValue) {
								return [
									{
										value: cellValue.toLowerCase(),
										info: this.validateSingleEmail(cellValue),
									},
									rowIndex,
								];
							} else {
								return [
									{
										value: '',
										info: undefined,
									},
									rowIndex,
								];
							}
						})
					);
				}, 0);
			});
		});
	}

	protected validateExistingTaskOrProjectId(cellValue: string | undefined): ValidationResult[] | undefined {
		let validationResult: ValidationResult[] | undefined;
		if (!cellValue || (cellValue.substring(0, 1) !== 'P' && cellValue.substring(0, 1) !== 'T')) {
			validationResult = [
				{
					level: 'error',
					message:
						'Incorrect format. Must be a task id that starts with a T or a project id that starts with a P (e.g. P17 or T324).',
				},
			];
		} else if (cellValue.substring(0, 1) === 'T' && !this.tasks.includes(cellValue)) {
			validationResult = [
				{
					level: 'error',
					message: 'Could not be matched to any existing task in your account.',
				},
			];
		} else if (cellValue.substring(0, 1) === 'P' && !this.projects.includes(cellValue)) {
			validationResult = [
				{
					level: 'error',
					message: 'Could not be matched to any existing project in your account.',
				},
			];
		}
		return validationResult;
	}

	public registerEntryChange(): void {
		this.nuvoImporter.registerEntryChange(row => {
			return this.validateRowChanged(row);
		});
	}

	private getEntryChangeForResult(_value: unknown, validationResult: ValidationResult[] | undefined): EntryChange {
		return {
			value: undefined, // We do not change the value, just validate it
			info: validationResult,
		};
	}

	private validateRowChanged(row: Row | undefined): RowChanged {
		return {
			existing_project_id_or_task_id:
				this.importMode === ImportMode.ImportTimeRegs &&
				this.getEntryChangeForResult(
					row?.existing_project_id_or_task_id,
					this.validateExistingTaskOrProjectId(row?.existing_project_id_or_task_id)
				),
			existing_project_id:
				this.importMode === ImportMode.ImportPhasesTasksTimeRegs &&
				this.getEntryChangeForResult(row?.existing_project_id, this.validateProjectId(row?.existing_project_id)),
			time_registration_person_email: this.getEntryChangeForResult(
				row?.time_registration_person_email,
				this.validateSingleEmail(row?.time_registration_person_email)
			),
			task_owner_email:
				this.importMode !== ImportMode.ImportTimeRegs &&
				this.getEntryChangeForResult(row?.task_owner_email, this.validateSingleEmail(row?.task_owner_email)),
			task_assigned_comma_seperated_emails:
				this.importMode !== ImportMode.ImportTimeRegs &&
				this.getEntryChangeForResult(
					row?.task_assigned_comma_seperated_emails,
					this.validateEmailCommaSeparatedList(row?.task_assigned_comma_seperated_emails)
				),
		};
	}

	protected validateProjectId(projectId: string | undefined): ValidationResult[] | undefined {
		let validationResult: ValidationResult[] | undefined = undefined;
		if (!projectId || projectId.substring(0, 1) !== 'P') {
			validationResult = [
				{
					level: 'error',
					message: 'Incorrect format. Must be a project id that starts with a P (e.g. P17).',
				},
			];
		} else if (projectId.substring(0, 1) === 'P' && !this.projects.includes(projectId)) {
			validationResult = [
				{
					level: 'error',
					message: 'Could not be matched to any existing project in your account.',
				},
			];
		}
		return validationResult;
	}

	private validateEmailCommaSeparatedList(cellValue): ValidationResult[] | undefined {
		const companyId = Util.getIdFromBase64String(this.companyIdBase64);
		let validationResult: ValidationResult[] | undefined;
		if (cellValue === undefined || cellValue === null || cellValue === '') {
			validationResult = [];
		} else {
			cellValue
				.replaceAll(' ', '')
				.split(',')
				.forEach(r => {
					if (!this.emails.includes(r.toLowerCase()) && !this.emails.includes(companyId + '_' + r.toLowerCase())) {
						validationResult = [
							{
								level: 'info',
								message: 'This email does not exist on your account and will be created as a deactivated user.',
							},
						];
						const emailReg = /^\S+@\S+\.\S+$/;
						const isEmailValid = emailReg.test(r);
						if (!isEmailValid) {
							validationResult = [
								{
									level: 'error',
									message:
										'The email is invalid. Please enter a valid email address, error.g. max@company.com.',
								},
							];
						}
					}
				});
		}
		return validationResult;
	}

	protected validateSingleEmail(cellValue: string | undefined): ValidationResult[] | undefined {
		const companyId = Util.getIdFromBase64String(this.companyIdBase64);
		let info: ValidationResult[] | undefined;
		const emailReg = /^\S+@\S+\.\S+$/;
		const isEmailValid = emailReg.test(cellValue as string);
		if (
			isEmailValid &&
			!this.emails.includes((cellValue as string).toLowerCase()) &&
			!this.emails.includes(companyId + '_' + (cellValue as string).toLowerCase())
		) {
			info = [
				{
					level: 'info',
					message: 'This email does not exist on your account and will be created as a deactivated user.',
				},
			];
		}
		return info;
	}
}
